/**
 * Single source of truth for which routes are reachable without auth.
 *
 * Used by two places that must agree:
 *  - middleware.ts, to decide whether to redirect to /login
 *  - context/Auth.tsx, to decide whether the initial auth check is allowed to
 *    replace the page with a loading spinner
 *
 * If these two lists ever drift, a public page can end up server-rendering a
 * spinner instead of its content — invisible to users (the client fills it in)
 * but not to crawlers, which then index an empty page.
 */
export const PUBLIC_PATHS = [
  '/',
  '/login',
  '/signup',
  '/join',
  '/reset-password',
  '/setup-password',
  '/complete-invitation',
  '/login-business',
  '/signup-business',
  '/unsubscribe',
  '/simple',
  '/payment/success',
  '/guidelines/forums',
  '/guidelines/marketplace',
  '/privacy-policy',
  '/cookies-policy',
  '/member-agreement',
  '/your-privacy-choices',
  '/accessibility',
  '/legal',
  '/about',
  '/pricing',
  '/resources',
  '/help-center',
  '/testimonials',
  '/waitlist',
  '/public',
  '/logo-preview',
];

export const STATIC_PREFIXES = [
  '/_next',
  '/api',
  '/favicon',
  '/assets',
  '/images',
  '/fonts',
  // App-link association files for the iOS and Android apps. The matcher in
  // middleware.ts already lets assetlinks.json through (it excludes anything
  // with a file extension), but apple-app-site-association deliberately has no
  // extension, so without this it would be redirected to /login and Apple would
  // never validate the domain.
  '/.well-known',
];

export function isPublicPath(pathname: string): boolean {
  if (STATIC_PREFIXES.some((p) => pathname.startsWith(p))) return true;
  return PUBLIC_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  );
}
